import React, { useCallback } from 'react';
import { NUDGE_MS, NUDGE_SHIFT_MS } from './lyricTimingFormat';

interface UseLyricEditorKeysProps {
  lineCount: number;
  /** The selected line, or null. */
  selected: number | null;
  select: (index: number) => void;
  togglePlay: () => void;
  /** False while a sync runs: the keys that change a time do nothing. */
  canEdit: boolean;
  nudge: (index: number, deltaMs: number) => void;
  /** Sets the start of the line to the play head. */
  setToPlayhead: (index: number) => void;
}

/** A key that types into a field is not a command of the editor. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * The keys of the sync editor (design s8): Space plays or pauses, ↑ and ↓ select a line, [ and ]
 * move the start of the selected line by 0.1 s (with Shift 0.5 s), and T sets the start of the
 * selected line to the play head. The brackets are read from `event.code`, because Shift turns
 * them into braces on many keyboards.
 */
export function useLyricEditorKeys({
  lineCount,
  selected,
  select,
  togglePlay,
  canEdit,
  nudge,
  setToPlayhead,
}: UseLyricEditorKeysProps) {
  return useCallback(
    (event: React.KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || isTypingTarget(event.target)) {
        return;
      }
      const step = event.shiftKey ? NUDGE_SHIFT_MS : NUDGE_MS;
      const isEarlier = event.code === 'BracketLeft' || event.key === '[' || event.key === '{';
      const isLater = event.code === 'BracketRight' || event.key === ']' || event.key === '}';
      const isSet = event.code === 'KeyT' || event.key === 't' || event.key === 'T';

      if (event.key === ' ' || event.code === 'Space') {
        event.preventDefault();
        togglePlay();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (lineCount === 0) {
          return;
        }
        event.preventDefault();
        const down = event.key === 'ArrowDown';
        if (selected === null) {
          select(down ? 0 : lineCount - 1);
          return;
        }
        select(Math.min(lineCount - 1, Math.max(0, selected + (down ? 1 : -1))));
        return;
      }
      if (selected === null || !canEdit) {
        return;
      }
      if (isEarlier || isLater) {
        event.preventDefault();
        nudge(selected, isEarlier ? -step : step);
        return;
      }
      if (isSet) {
        event.preventDefault();
        setToPlayhead(selected);
      }
    },
    [canEdit, lineCount, nudge, select, selected, setToPlayhead, togglePlay]
  );
}
